import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import {
  Landmark, Plus, Check, Clock, AlertTriangle, Trash2, Star,
  Loader2, ChevronRight, X,
} from 'lucide-react';
import { PlaidSection, InstitutionalTransferSection } from './BankLinkingMethods';

const money = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n || 0);

const STATUS_META = {
  active:  { label: 'Active',  icon: Check,          cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  pending: { label: 'Pending', icon: Clock,          cls: 'bg-amber-50 border-amber-200 text-amber-700' },
  failed:  { label: 'Failed',  icon: AlertTriangle,  cls: 'bg-red-50 border-red-200 text-red-700' },
};

const STATUS_DESCRIPTION = {
  active:  'Verified and ready for disbursement.',
  pending: 'Awaiting micro-deposit verification.',
  failed:  'Micro-deposit verification failed. Remove and re-add the account.',
};


// ────────────────────────────────────────────────────────────────
// Add Account Modal (wizard: entry → confirm-init → awaiting-verify)
// ────────────────────────────────────────────────────────────────
function AddAccountModal({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1=entry, 2=initiated
  const [routing, setRouting] = useState('');
  const [account, setAccount] = useState('');
  const [accountType, setAccountType] = useState('checking');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [created, setCreated] = useState(null);

  const reset = () => {
    setStep(1); setRouting(''); setAccount(''); setAccountType('checking');
    setSubmitting(false); setErr(''); setCreated(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    setErr('');
    if (!/^\d{9}$/.test(routing)) { setErr('Routing number must be exactly 9 digits.'); return; }
    if (!/^\d{4,17}$/.test(account)) { setErr('Account number must be 4–17 digits.'); return; }

    setSubmitting(true);
    try {
      const res = await api.post('/participant/bank-accounts', {
        routing, account, type: accountType,
      });
      setCreated(res.data);
      setStep(2);
      onCreated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(Array.isArray(detail) ? detail[0]?.msg || 'Validation failed' : detail || 'Failed to link account.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const inputClass = 'w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm font-mono text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="add-bank-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E5E9]">
          <h3 className="text-sm font-semibold text-[#1B2A4A]">Link a Bank Account</h3>
          <button onClick={handleClose} className="text-[#9CA3AF] hover:text-[#374151]" data-testid="close-add-modal">
            <X size={16} />
          </button>
        </div>

        {step === 1 && (
          <div className="p-5 space-y-4">
            <ol className="flex items-center gap-2 text-[10px] text-[#6B7280] mb-1">
              <li className="font-semibold text-[#1B2A4A]">1 · Enter details</li>
              <ChevronRight size={10} />
              <li>2 · Micro-deposits sent</li>
              <ChevronRight size={10} />
              <li>3 · Verify amounts</li>
            </ol>

            {err && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-3 py-2"
                   data-testid="add-bank-error">
                <AlertTriangle size={13} className="text-red-600" />
                <span className="text-xs text-red-700">{err}</span>
              </div>
            )}

            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Routing Number (9 digits)</label>
              <input type="text" inputMode="numeric" maxLength={9} value={routing}
                     onChange={e => setRouting(e.target.value.replace(/\D/g, ''))}
                     placeholder="021000021" className={inputClass} data-testid="input-routing" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Account Number</label>
              <input type="text" inputMode="numeric" maxLength={17} value={account}
                     onChange={e => setAccount(e.target.value.replace(/\D/g, ''))}
                     placeholder="555123456" className={inputClass} data-testid="input-account" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Account Type</label>
              <div className="grid grid-cols-2 gap-2">
                {['checking', 'savings'].map(t => (
                  <button key={t} type="button" onClick={() => setAccountType(t)}
                          data-testid={`type-${t}`}
                          className={`h-10 rounded border text-xs font-medium capitalize transition-colors ${accountType === t ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]' : 'bg-white text-[#6B7280] border-[#D1D5DB] hover:border-[#1B2A4A]'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2">
              <p className="text-[11px] text-blue-700 leading-relaxed">
                We will initiate two micro-deposits (each under $1) to verify your account.
                They typically arrive within 1–3 business days.
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={handleClose}
                      className="flex-1 h-10 rounded text-xs font-medium text-[#6B7280] border border-[#D1D5DB] hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={submitting}
                      data-testid="submit-add-bank"
                      className="flex-1 h-10 rounded text-xs font-medium text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {submitting && <Loader2 size={12} className="animate-spin" />}
                Initiate Micro-Deposits
              </button>
            </div>
          </div>
        )}

        {step === 2 && created && (
          <div className="p-5 space-y-4" data-testid="add-bank-success">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                <Check size={16} className="text-emerald-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-[#1B2A4A]">Micro-deposits initiated</h4>
                <p className="text-xs text-[#6B7280]">{created.bankName} · {created.maskedAccount}</p>
              </div>
            </div>

            <p className="text-xs text-[#6B7280] leading-relaxed">
              Two small deposits will appear in your bank within 1–3 business days.
              Return here to verify their exact amounts.
            </p>

            {created.demo_deposits && (
              <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2.5" data-testid="demo-deposits-hint">
                <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">Demo Mode</p>
                <p className="text-xs text-amber-700 mt-1">
                  In production these arrive at your bank. For this demo, the amounts are{' '}
                  <b className="font-mono">{money(created.demo_deposits.deposit1)}</b> and{' '}
                  <b className="font-mono">{money(created.demo_deposits.deposit2)}</b>.
                </p>
              </div>
            )}

            <button onClick={handleClose}
                    data-testid="close-success-btn"
                    className="w-full h-10 rounded text-xs font-medium text-white bg-[#1B2A4A] hover:bg-[#0F1A33]">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Verify Modal — enter two micro-deposit amounts
// ────────────────────────────────────────────────────────────────
function VerifyModal({ acct, onClose, onVerified }) {
  const [d1, setD1] = useState('');
  const [d2, setD2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  if (!acct) return null;

  const inputClass = 'w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm font-mono text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20';

  const handleSubmit = async () => {
    setErr('');
    const n1 = parseFloat(d1), n2 = parseFloat(d2);
    if (!(n1 > 0 && n1 < 1) || !(n2 > 0 && n2 < 1)) {
      setErr('Each amount must be between $0.01 and $0.99.'); return;
    }
    setSubmitting(true);
    try {
      await api.post(`/participant/bank-accounts/${acct.id}/verify`, {
        deposit1: n1, deposit2: n2,
      });
      onVerified?.();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Verification failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="verify-bank-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E5E9]">
          <h3 className="text-sm font-semibold text-[#1B2A4A]">Verify Micro-Deposits</h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151]" data-testid="close-verify-modal">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-[#6B7280]">
            Enter the exact amounts of the two micro-deposits sent to{' '}
            <b className="text-[#1B2A4A]">{acct.bankName}</b> {acct.maskedAccount}. Either order is accepted.
          </p>

          {err && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="verify-error">
              <AlertTriangle size={13} className="text-red-600" />
              <span className="text-xs text-red-700">{err}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Deposit 1</label>
              <input type="number" step="0.01" min="0.01" max="0.99" value={d1}
                     onChange={e => setD1(e.target.value)}
                     placeholder="0.12" className={inputClass} data-testid="input-deposit-1" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Deposit 2</label>
              <input type="number" step="0.01" min="0.01" max="0.99" value={d2}
                     onChange={e => setD2(e.target.value)}
                     placeholder="0.34" className={inputClass} data-testid="input-deposit-2" />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose}
                    className="flex-1 h-10 rounded text-xs font-medium text-[#6B7280] border border-[#D1D5DB] hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={submitting}
                    data-testid="submit-verify"
                    className="flex-1 h-10 rounded text-xs font-medium text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {submitting && <Loader2 size={12} className="animate-spin" />}
              Verify &amp; Activate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Main panel
// ────────────────────────────────────────────────────────────────
export const BankAccountsPanel = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await api.get('/participant/bank-accounts');
      setAccounts(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Unable to load linked accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSetPrimary = async (id) => {
    setBusyId(id); setErr('');
    try {
      await api.put(`/participant/bank-accounts/${id}/primary`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Unable to set primary.');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this linked bank account?')) return;
    setBusyId(id); setErr('');
    try {
      await api.delete(`/participant/bank-accounts/${id}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Unable to remove account.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="bank-accounts-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-[#1B2A4A] uppercase tracking-wider">
          Linked Bank Accounts
        </h2>
        <button onClick={() => setShowAdd(true)}
                data-testid="add-bank-btn"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-medium text-[#1B2A4A] border border-[#D1D5DB] hover:border-[#1B2A4A] hover:bg-gray-50 transition-colors">
          <Plus size={13} /> Add Manually (micro-deposit)
        </button>
      </div>

      {/* Primary: Plaid (instant, verified) */}
      <PlaidSection onLinked={load} />

      {/* Alternative: direct firm-to-firm rollover */}
      <InstitutionalTransferSection />

      {err && (
        <div className="panel p-3 border-red-200 bg-red-50" data-testid="bank-panel-error">
          <p className="text-xs text-red-700">{err}</p>
        </div>
      )}

      <div className="panel p-0 overflow-hidden" data-testid="bank-accounts-table">
        {loading && accounts.length === 0 ? (
          <div className="p-6 text-center text-xs text-[#9CA3AF]">Loading…</div>
        ) : accounts.length === 0 ? (
          <div className="p-8 text-center" data-testid="no-bank-accounts">
            <Landmark size={28} className="mx-auto text-[#D1D5DB] mb-2" />
            <p className="text-sm text-[#6B7280]">No bank accounts linked yet.</p>
            <p className="text-[11px] text-[#9CA3AF] mt-1">
              Add a bank account to receive retirement withdrawals and loan disbursements.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E5E9] text-[10px] text-[#6B7280] uppercase tracking-wider">
                <th className="text-left py-3 px-4">Bank</th>
                <th className="text-left py-3 px-4">Account</th>
                <th className="text-left py-3 px-4">Type</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => {
                const meta = STATUS_META[a.status] || STATUS_META.pending;
                const StatusIcon = meta.icon;
                const rowBusy = busyId === a.id;
                return (
                  <tr key={a.id} className="border-b border-[#F0F1F3] hover:bg-[#FAFBFC]"
                      data-testid={`bank-row-${a.id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Landmark size={13} className="text-[#6B7280]" />
                        <span className="font-medium text-[#1B2A4A]" data-testid={`bank-name-${a.id}`}>{a.bankName}</span>
                        {a.isPrimary && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#1B2A4A] text-white"
                                data-testid={`primary-badge-${a.id}`}>
                            <Star size={9} /> PRIMARY
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-xs text-[#374151]" data-testid={`masked-${a.id}`}>
                      {a.maskedAccount}
                    </td>
                    <td className="py-3 px-4 text-xs text-[#6B7280] capitalize">{a.type}</td>
                    <td className="py-3 px-4">
                      <div>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded border ${meta.cls}`}
                              data-testid={`status-${a.id}`}>
                          <StatusIcon size={10} />
                          {meta.label}
                        </span>
                        <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                          {STATUS_DESCRIPTION[a.status] || ''}
                        </p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {a.status === 'pending' && (
                          <button onClick={() => setVerifyTarget(a)} disabled={rowBusy}
                                  data-testid={`verify-btn-${a.id}`}
                                  className="h-7 px-2.5 rounded text-[10px] font-medium text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50">
                            Verify
                          </button>
                        )}
                        {a.status === 'active' && !a.isPrimary && (
                          <button onClick={() => handleSetPrimary(a.id)} disabled={rowBusy}
                                  data-testid={`set-primary-${a.id}`}
                                  className="h-7 px-2.5 rounded text-[10px] font-medium text-[#1B2A4A] border border-[#D1D5DB] hover:border-[#1B2A4A] hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1">
                            <Star size={10} /> Set Primary
                          </button>
                        )}
                        <button onClick={() => handleRemove(a.id)} disabled={rowBusy}
                                data-testid={`remove-btn-${a.id}`}
                                title="Remove account"
                                className="h-7 w-7 rounded text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 disabled:opacity-50 inline-flex items-center justify-center">
                          {rowBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AddAccountModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={load}
      />

      <VerifyModal
        acct={verifyTarget}
        onClose={() => setVerifyTarget(null)}
        onVerified={load}
      />
    </div>
  );
};

export default BankAccountsPanel;
